import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tooltip, Chip, TextField, AppBar, Toolbar,
} from '@mui/material';
import MapIcon from '@mui/icons-material/Map';
import { format, isWeekend } from 'date-fns';
import { getDefaultLayout, getAllSeatsFromLayout, CABINS } from '../config/floorLayout';
import axios from 'axios';
import './FloorPlan.css';

const DEFAULT_FIXED_SEATS = [];

const getContrastColor = (hexColor) => {
  if (!hexColor) return '#fff';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
};

const PublicFloorPlan = () => {
  const [layout] = useState(getDefaultLayout());
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [bookings, setBookings] = useState([]);
  const [fixedSeatsFromDB, setFixedSeatsFromDB] = useState(null);

  const allFixedSeats = fixedSeatsFromDB !== null ? fixedSeatsFromDB : DEFAULT_FIXED_SEATS;
  const ALL_SEATS = getAllSeatsFromLayout(layout);

  const fetchData = useCallback(async () => {
    try {
      const apiBase = (import.meta.env.VITE_API_BASE_URL || 'https://gssapi.valueback.ai') + '/api';
      const res = await axios.get(`${apiBase}/bookings/public?date=${selectedDate}`);
      setBookings(res.data.bookings || []);
      setFixedSeatsFromDB(res.data.fixedSeats || []);
    } catch (err) {
      console.error('Failed to fetch public bookings:', err);
    }
  }, [selectedDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getBookingInfo = (seatNumber) => {
    return bookings.find(b => b.seat_number === seatNumber);
  };

  const getSeatStatus = (seatNumber) => {
    if (allFixedSeats.includes(seatNumber)) return 'fixed';
    const booking = getBookingInfo(seatNumber);
    if (booking) return 'booked';
    return 'available';
  };

  // Stats
  const totalSeats = ALL_SEATS.length;
  const fixedCount = allFixedSeats.length;
  const bookedCount = bookings.length;
  const availableCount = totalSeats - fixedCount - bookedCount;

  // Leader legend
  const leaderColors = {};
  bookings.forEach(b => {
    const uid = b.user_abbreviation || '?';
    if (!leaderColors[uid]) {
      leaderColors[uid] = {
        name: b.user_name || 'Unknown',
        abbr: b.user_abbreviation || '?',
        color: b.user_color || '#f44336',
        count: 0,
      };
    }
    leaderColors[uid].count++;
  });

  const handleDateChange = (e) => {
    const d = new Date(e.target.value + 'T00:00:00');
    if (!isWeekend(d)) setSelectedDate(e.target.value);
  };

  const renderSeat = (seatNumber) => {
    if (seatNumber === null) return <div className="seat-empty" key={`e-${Math.random()}`} />;

    const status = getSeatStatus(seatNumber);
    const booking = getBookingInfo(seatNumber);
    const leaderColor = booking?.user_color;
    const leaderAbbr = booking?.user_abbreviation;

    let seatStyle = {};
    let seatClass = 'seat';

    if (status === 'booked' && leaderColor) {
      seatStyle = {
        backgroundColor: leaderColor,
        color: getContrastColor(leaderColor),
        borderColor: leaderColor,
        cursor: 'default',
      };
    } else if (status === 'fixed') {
      seatClass += ' seat-fixed';
      seatStyle.cursor = 'default';
    } else {
      seatClass += ' seat-available';
      seatStyle.cursor = 'default';
    }

    const tooltipTitle =
      status === 'booked'
        ? `Seat ${seatNumber} - ${booking?.user_name || 'Unknown'} (${leaderAbbr || ''})`
        : status === 'fixed'
        ? `Seat ${seatNumber} - Fixed/Reserved`
        : `Seat ${seatNumber} - Available`;

    return (
      <Tooltip title={tooltipTitle} arrow key={seatNumber}>
        <div className={seatClass} style={seatStyle}>
          {status === 'booked' && leaderAbbr ? leaderAbbr : seatNumber}
        </div>
      </Tooltip>
    );
  };

  const renderSeatRow = (seats, keyPrefix) => (
    <div className="seat-row" key={keyPrefix}>
      {seats.map(seat => renderSeat(seat))}
    </div>
  );

  const renderZone = (rows, keyPrefix) => (
    <div className="zone-block" key={keyPrefix}>
      {rows.map((row, idx) => renderSeatRow(row, `${keyPrefix}-row-${idx}`))}
    </div>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#052e2b' }}>
        <Toolbar>
          <MapIcon sx={{ mr: 1.5 }} />
          <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
            GSS Floor Plan - Live View
          </Typography>
          <Chip
            label={format(new Date(selectedDate + 'T00:00:00'), 'EEEE, dd MMM yyyy')}
            sx={{ backgroundColor: '#68DA6A', color: '#052e2b', fontWeight: 600, mr: 2 }}
          />
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={handleDateChange}
            sx={{
              '& .MuiInputBase-root': { backgroundColor: 'white', borderRadius: 2 },
              width: 160,
            }}
          />
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Stats */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`Total: ${totalSeats}`} size="small" variant="outlined" />
              <Chip label={`Available: ${availableCount}`} size="small" sx={{ backgroundColor: '#68DA6A', color: '#052e2b' }} />
              <Chip label={`Booked: ${bookedCount}`} size="small" sx={{ backgroundColor: '#f44336', color: 'white' }} />
              <Chip label={`Fixed: ${fixedCount}`} size="small" sx={{ backgroundColor: '#9e9e9e', color: 'white' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <div className="legend-box legend-available" />
                <Typography variant="caption">Available</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <div className="legend-box legend-fixed" />
                <Typography variant="caption">Fixed</Typography>
              </Box>
            </Box>
          </Box>
          {Object.keys(leaderColors).length > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
                Team Leaders:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {Object.values(leaderColors).map(leader => (
                  <Chip
                    key={leader.abbr}
                    label={`${leader.abbr} - ${leader.name} (${leader.count})`}
                    size="small"
                    sx={{
                      backgroundColor: leader.color,
                      color: getContrastColor(leader.color),
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Floor Plan */}
        <Paper sx={{ p: 3, overflowX: 'auto' }}>
          <div className="floor-plan-wrapper">
            <div className="floor-plan-main">
              <div className="floor-upper">
                <div className="zone-container">
                  <Typography variant="subtitle2" className="zone-label" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                    Zone C (114-181)
                  </Typography>
                  {renderZone(layout.zoneAUpper, 'zone-a-upper')}
                </div>
                <div className="rooms-middle">
                  <div className="rooms-strip">
                    <div className="room-block server-room">
                      <Typography variant="caption" fontWeight={600}>Server Room</Typography>
                    </div>
                    <div className="room-block meeting-room">
                      <Typography variant="caption" fontWeight={600}>Ravi</Typography>
                    </div>
                    <div className="room-block meeting-room">
                      <Typography variant="caption" fontWeight={600}>Sutlej</Typography>
                    </div>
                    <div className="room-block meeting-room">
                      <Typography variant="caption" fontWeight={600}>Saraswati</Typography>
                    </div>
                    <div className="room-block entry-block">
                      <Typography variant="caption" fontWeight={700}>Entry to<br/>the floor</Typography>
                    </div>
                  </div>
                </div>
                <div className="zone-container">
                  <Typography variant="subtitle2" className="zone-label" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                    Zone A (1-74)
                  </Typography>
                  <div className="zone-c-upper-spacer" />
                  {renderZone(layout.zoneCUpper, 'zone-c-upper')}
                </div>
              </div>
              <div className="corridor">
                <Typography variant="caption" sx={{ color: '#999', letterSpacing: 4, textTransform: 'uppercase' }}>
                  Corridor
                </Typography>
              </div>
              <div className="floor-lower">
                <div className="zone-container">
                  {renderZone(layout.zoneALower, 'zone-a-lower')}
                </div>
                <div className="zone-container zone-b-container">
                  {renderZone(layout.zoneB, 'zone-b')}
                  <Typography variant="subtitle2" className="zone-label" sx={{ mt: 1, fontWeight: 700, color: '#052e2b' }}>
                    Zone B (75-113)
                  </Typography>
                </div>
                <div className="zone-container">
                  {renderZone(layout.zoneCLower, 'zone-c-lower')}
                </div>
              </div>
            </div>
            <div className="cabins-column">
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                Cabins (Fixed)
              </Typography>
              {CABINS.map(cabin => (
                <div className="cabin-block" key={cabin.name}>
                  <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                    {cabin.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>
                    ({cabin.seats} seat{cabin.seats > 1 ? 's' : ''})
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </Paper>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
          Auto-refreshes every 30 seconds
        </Typography>
      </Box>
    </Box>
  );
};

export default PublicFloorPlan;
